import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import heroClip from "../../assets/landing/hero-picker.webm";
import heroPoster from "../../assets/landing/hero-picker-poster.png";

/** The three control modes — the product's sharpest differentiator (the dial),
 *  surfaced in the hero as an interactive control over the artifact. AI is
 *  the *third position*, never the headline (brief anti-pattern #1). */
const MODES = [
  {
    key: "manual",
    label: "Manual",
    line: "You size each member. The engine checks every one against the governing code clause.",
    gate: "You drive every step",
  },
  {
    key: "automated",
    label: "Automated",
    line: "The engine sizes the structure to your saved standards. You review the package.",
    gate: "Ready for your review",
  },
  {
    key: "ai",
    label: "AI",
    line: "AI supervises the run, asks when a call is judgment, and learns your preferences. You stamp.",
    gate: "Ready for your stamp",
  },
] as const;

export function Hero() {
  const { state } = useAuth();
  const authed = state.kind === "authenticated";
  const [mode, setMode] = useState<(typeof MODES)[number]["key"]>("automated");
  const active = MODES.find((m) => m.key === mode) ?? MODES[1];

  return (
    <section className="landing-wrap landing-hero">
      <div className="hero-banner">
        <div className="hero-grid" aria-hidden="true" />

        <div className="hero-banner-inner hero-banner-inner--split">
          <div className="hero-content">
            <span className="hero-eyebrow">The structural engineering &amp; design harness</span>
            <h1 className="hero-title">
              Stamp-ready structural plans and calcs from your floor plan.{" "}
              <span className="hero-title-soft">You set the standards, AI supervises.</span>
            </h1>
            <p className="hero-sub">
              One harness for design and code-traceable calcs, with three control modes:{" "}
              <strong>manual, automated, or AI-supervised</strong>. Your licensed PE reviews and
              stamps; AI never does.*
            </p>
            <div className="hero-cta">
              <Link to="/beta?intent=demo" className="btn btn-lg fm-on-dark">Book a demo</Link>
              {authed ? (
                <Link to="/app" className="btn btn-lg fm-ghost-dark">Open the app</Link>
              ) : (
                <Link to="/beta?intent=waitlist" className="btn btn-lg fm-ghost-dark">Join the beta</Link>
              )}
            </div>
            <p className="hero-signin hero-demo-note">
              Bring your floor plan, watch it run live.{" "}
              <Link to="/plans" className="hero-alt-link">Or send us the plan and we&apos;ll run it →</Link>
            </p>
            <p className="hero-stamp-note">
              *Not every jurisdiction requires a stamp; where one isn&apos;t required, plans
              typically go straight to permitting.
            </p>
          </div>

          <HeroArtifact mode={mode} setMode={setMode} active={active} />
        </div>
      </div>
    </section>
  );
}

type ArtifactProps = {
  mode: string;
  setMode: (m: (typeof MODES)[number]["key"]) => void;
  active: (typeof MODES)[number];
};

/** The floor-plan → annotated plan + calc transformation, steered by the
 *  Manual / Automated / AI control. Sample/placeholder geometry; structured
 *  like the real viewer output. */
function HeroArtifact({ mode, setMode, active }: ArtifactProps) {
  return (
    <div className="hero-artifact">
      <div className="hero-dial" role="group" aria-label="Control mode (preview)">
        {MODES.map((m) => (
          <button
            key={m.key}
            type="button"
            className={`hero-dial-btn${mode === m.key ? " is-on" : ""}`}
            aria-pressed={mode === m.key}
            onClick={() => setMode(m.key)}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="hero-canvas hero-canvas--video">
        <span className="hero-canvas-tag">Real product · sizing a rafter, options scored live</span>
        <video
          className="hero-video"
          src={heroClip}
          poster={heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label="The rafter size-and-grade selector scoring 167 catalog sections live against the member's loads, filtering to passing options, and picking the lightest one"
          ref={(el) => {
            // Honor prefers-reduced-motion: hold on the poster frame.
            if (el && window.matchMedia("(prefers-reduced-motion: reduce)").matches) el.pause();
          }}
        />
      </div>

      <div className="hero-readout">
        <div className="hero-schedule">
          <div className="hero-readout-row">
            <span className="hero-readout-mono">Rafter 2×10 DF-L #2</span>
            <span className="hero-readout-dcr">DCR 0.86 ✓</span>
          </div>
          <div className="hero-readout-row">
            <span className="hero-readout-mono">Ridge 5-1/4×11-7/8 24F GLB</span>
            <span className="hero-readout-dcr">DCR 0.74 ✓</span>
          </div>
          <div className="hero-readout-row">
            <span className="hero-readout-mono">Shear wall SW-3</span>
            <span className="hero-readout-dcr">DCR 0.68 ✓</span>
          </div>
        </div>
        <div aria-live="polite" aria-atomic="true">
          <p className="hero-readout-line">{active.line}</p>
          <span className={`hero-gate hero-gate--${mode}`}>
            <span className="hero-gate-dot" aria-hidden="true" />
            {active.gate}
          </span>
        </div>
      </div>
    </div>
  );
}

